import React, { useState, useEffect } from 'react';
import Papa from 'papaparse';
import './TeamRegistrationDisplay.css';

const TeamRegistrationDisplay = () => {
  // Memoize initial state to reduce unnecessary re-renders
  const [teams, setTeams] = useState({
    Mix: [],
    Moterys: [],
    Vyrai: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dataSource, setDataSource] = useState(null);
  
  // Static CSV URL - hardcoded with direct access parameter
  const staticCsvUrl = "https://docs.google.com/spreadsheets/d/e/2PACX-1vRTf9GNjA8dVteAowR4JqYC_DtFx3p_I1fYH61cJf3V5Qw1r4FAd42xYJM-nNRts0ZZLIczVY4GeB71/pub?gid=741767260&single=true&output=csv&r=" + Math.floor(Math.random() * 100000);
  
  // Track which sections are expanded (all collapsed by default)
  const [expandedSections, setExpandedSections] = useState({
    Mix: false,
    Moterys: false,
    Vyrai: false
  });

  // Toggle section expansion
  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Process team data more efficiently
  const processTeamData = (data) => {
    const groupedTeams = {
      Mix: [],
      Moterys: [],
      Vyrai: []
    };
    
    // Faster array processing with reduced operations
    for (let i = 0; i < data.length; i++) {
      const row = data[i];
      const group = row['Pasirinkite grupę'] || 'Mix'; // Default to Mix if empty
      
      // Skip processing if group doesn't exist in our structure
      if (!(group in groupedTeams)) continue;
      
      const team1Member = row['Pirmojo komandos nario vardas ir pavardė'] || '';
      const team2Member = row['Antrojo komandos nario vardas ir pavardė'] || '';
      
      // Skip empty teams
      if (!team1Member && !team2Member) continue;
      
      // Add team directly to appropriate group
      groupedTeams[group].push({
        member1: team1Member,
        member2: team2Member,
        level: row['Komandos lygis'] || 'Not specified'
      });
    }
    
    // Update state in one batch operation
    setTeams(groupedTeams);
    setLoading(false);
  };

  // Main data loading function - optimized
  const loadData = async (url) => {
    try {
      if (!url) {
        throw new Error("No CSV URL provided");
      }
      
      // Add cache-busting parameter to avoid potential caching issues
      const cacheBustUrl = `${url}${url.includes('?') ? '&' : '?'}_cb=${Date.now()}`;
      
      // Use faster fetch options
      const response = await fetch(cacheBustUrl, {
        method: 'GET',
        cache: 'no-store', // Bypass cache
        priority: 'high', // Prioritize this fetch
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const csvText = await response.text();
      setDataSource(url);
      
      // Parse the CSV with optimized settings
      Papa.parse(csvText, {
        header: true,
        skipEmptyLines: true,
        fastMode: true, // Use fast mode for simple CSV parsing
        delimitersToGuess: [',', '\t', '|', ';'], // Handle different delimiters
        complete: (results) => {
          processTeamData(results.data);
        },
        error: (parseError) => {
          throw new Error(`CSV parsing error: ${parseError.message}`);
        }
      });
    } catch (error) {
      console.error('Error loading data:', error);
      setError(`Failed to load data: ${error.message}`);
      setLoading(false);
    }
  };

  // Load data immediately on component mount with cleanup
  useEffect(() => {
    let isMounted = true;
    
    // Set initial loading state
    setLoading(true);
    
    // Add an AbortController to cancel fetch if component unmounts
    const controller = new AbortController();
    const signal = controller.signal;
    
    // Start loading data after a minimal delay to allow UI to render first
    const loadingTimer = setTimeout(() => {
      if (isMounted) {
        loadData(staticCsvUrl);
      }
    }, 10);
    
    // Cleanup function
    return () => {
      isMounted = false;
      clearTimeout(loadingTimer);
      controller.abort();
    };
  }, []);

  // Group title maps for better display
  const groupTitles = {
    Mix: 'Mixed Teams',
    Moterys: 'Women\'s Teams',
    Vyrai: 'Men\'s Teams'
  };

  // Level badge component
  const LevelBadge = ({ level }) => {
    const getBadgeClass = (level) => {
      switch(level.toLowerCase()) {
        case 'beginner': return 'level-badge level-beginner';
        case 'intermediate': return 'level-badge level-intermediate';
        case 'advanced': return 'level-badge level-advanced';
        default: return 'level-badge level-default';
      }
    };
    
    return (
      <span className={getBadgeClass(level)}>
        {level}
      </span>
    );
  };

  // Improved loading state with fallback timer
  const [longLoading, setLongLoading] = useState(false);
  
  // Set a flag for extended loading time
  useEffect(() => {
    let timer;
    if (loading) {
      // If loading takes more than 1.5 seconds, show enhanced loading indicator
      timer = setTimeout(() => setLongLoading(true), 1500);
    } else {
      setLongLoading(false);
    }
    return () => clearTimeout(timer);
  }, [loading]);

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        {longLoading && (
          <p style={{ marginTop: '1rem', color: '#888' }}>
            Loading data... This may take a moment.
          </p>
        )}
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-container">
        <div className="error-message">
          <p>{error}</p>
        </div>
      </div>
    );
  }

  // Chevron icon components for expand/collapse
  const ChevronDown = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="chevron-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
    </svg>
  );
  
  const ChevronRight = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="chevron-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
    </svg>
  );

  const Calendar = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
      <line x1="16" y1="2" x2="16" y2="6"></line>
      <line x1="8" y1="2" x2="8" y2="6"></line>
      <line x1="3" y1="10" x2="21" y2="10"></line>
    </svg>
  );

  const Users = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
      <circle cx="9" cy="7" r="4"></circle>
      <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
      <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
    </svg>
  );

  const Award = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="award-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="8" r="7"></circle>
      <polyline points="8.21 13.89 7 23 12 20 17 23 15.79 13.88"></polyline>
    </svg>
  );

  return (
    <div className="container">
      <header className="header">
        <div className="header-background"></div>
        <div className="header-stats">
          <div className="header-stat">
            <Users />
            <span>{Object.values(teams).flat().length} Komandos</span>
          </div>
        </div>
      </header>
      <div className="groups-container">
        {Object.entries(teams).map(([groupName, teamList]) => (
          <div 
            key={groupName} 
            className={`group-section group-${groupName.toLowerCase()}`}
          >
            <div 
              className={`group-header header-${groupName.toLowerCase()}`}
              onClick={() => toggleSection(groupName)}
            >
              <div className="group-header-content">
                <div className="group-icon-container">
                  <Users />
                </div>
                <div>
                  <h2>{groupTitles[groupName]}</h2>
                  <p className="team-count">{teamList.length} teams registered</p>
                </div>
              </div>
              <div className="chevron">
                {expandedSections[groupName] ? <ChevronDown /> : <ChevronRight />}
              </div>
            </div>
            
            <div 
              className={`group-content ${expandedSections[groupName] ? 'expanded' : 'collapsed'}`}
            >
              {teamList.length > 0 ? (
                <div className="team-grid">
                  {teamList.map((team, index) => (
                    <div key={index} className={`team-card team-card-${groupName.toLowerCase()}`}>
                      <div className="team-members">
                        <div className="member-name">{team.member1}</div>
                        {team.member2 && (
                          <>
                            <div className="member-divider"></div>
                            <div className="member-name">{team.member2}</div>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="no-teams">No teams registered</p>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default TeamRegistrationDisplay;